/**
 * G2eGente - Selecionar Candidatos
 * Busca de talentos para seleção
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout, companyMenuItems } from '../../components/DashboardLayout';
import { candidatesAPI, jobsAPI, trabalhoEventualAPI, cartasAPI, partnersAPI } from '../../lib/api';
import { useFeatureAccess, BlockedFeatureOverlay } from '../../components/FeatureGate';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { 
  Search, 
  Loader2,
  Users,
  Filter,
  MapPin,
  Briefcase,
  Brain,
  X,
  ChevronRight,
  User,
  GraduationCap,
  Heart,
  Clock,
  Landmark,
  FileText,
  Calendar,
  Building2
} from 'lucide-react';
import { toast } from 'sonner';
import { JobInvitePanel } from '../../components/candidates/JobInvitePanel';
import { CandidateContactCard } from '../../components/candidates/CandidateContactCard';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { CargoBannersTab } from '../../components/candidates/CargoBannersTab';

export default function CandidateSelect() {
  const navigate = useNavigate();
  const { hasAccess, loading: featureLoading } = useFeatureAccess('selecao_aberta');
  const [candidates, setCandidates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [partners, setPartners] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [categoriasEventual, setCategoriasEventual] = useState([]);
  
  const [mainTab, setMainTab] = useState('buscar');
  const [inviteRefresh, setInviteRefresh] = useState(0);

  // Carta de Encaminhamento Dialog
  const [showCartaDialog, setShowCartaDialog] = useState(false);
  const [cartaData, setCartaData] = useState({
    candidate: null,
    job: null,
    data_entrevista: '',
    hora_entrevista: '',
    local_entrevista: '',
    entrevistador: '',
    documentos_necessarios: 'Carteira de Trabalho (física ou digital), RG, CPF',
    observacoes: ''
  });
  const [savingCarta, setSavingCarta] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    cargo: '',
    localizacao: '',
    disc_profile: 'all',
    pcd: 'all',
    estagiario: 'all',
    aprendiz: 'all',
    eventual: false,
    categoria_eventual: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchJobs();
    fetchCategoriasEventual();
    fetchPartners();
  }, []);
  
  const fetchPartners = async () => {
    try {
      const res = await partnersAPI.getPublicList();
      // Create a map for quick lookup
      const partnersMap = {};
      (res.data || []).forEach(p => {
        partnersMap[p.id] = p;
      });
      setPartners(partnersMap);
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  const fetchJobs = async () => {
    try {
      const res = await jobsAPI.getMy();
      setJobs(res.data.filter(j => j.status === 'em_andamento'));
    } catch (error) {
      console.error('Erro ao carregar vagas:', error);
    }
  };

  const fetchCategoriasEventual = async () => {
    try {
      const res = await trabalhoEventualAPI.getCategorias();
      setCategoriasEventual(res.data.categorias || []);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  const handleSearch = async () => {
    // Require at least one filter
    const hasFilter = filters.cargo || filters.localizacao || 
      (filters.disc_profile && filters.disc_profile !== 'all') ||
      (filters.pcd && filters.pcd !== 'all') ||
      (filters.estagiario && filters.estagiario !== 'all') ||
      (filters.aprendiz && filters.aprendiz !== 'all') ||
      filters.eventual;
    
    if (!hasFilter) {
      toast.error('Preencha pelo menos um filtro para buscar');
      return;
    }

    setLoading(true);
    try {
      const params = {};
      if (filters.cargo) params.cargo = filters.cargo;
      if (filters.localizacao) params.localizacao = filters.localizacao;
      if (filters.disc_profile && filters.disc_profile !== 'all') params.disc_profile = filters.disc_profile;
      if (filters.pcd && filters.pcd !== 'all') params.pcd = filters.pcd;
      if (filters.estagiario && filters.estagiario !== 'all') params.estagiario = filters.estagiario;
      if (filters.aprendiz && filters.aprendiz !== 'all') params.aprendiz = filters.aprendiz;
      // Trabalho Eventual
      if (filters.eventual) {
        params.eventual = true;
        if (filters.categoria_eventual && filters.categoria_eventual !== 'all') {
          params.categoria_eventual = filters.categoria_eventual;
        }
      }

      const res = await candidatesAPI.search(params);
      setCandidates(res.data);
      
      if (res.data.length === 0) {
        toast.info('Nenhum candidato encontrado com esses critérios');
      }
    } catch (error) {
      toast.error('Erro ao buscar candidatos');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      cargo: '',
      localizacao: '',
      disc_profile: 'all',
      pcd: 'all',
      estagiario: 'all',
      aprendiz: 'all',
      eventual: false,
      categoria_eventual: 'all'
    });
    setCandidates([]);
  };

  const handleSelectForJob = async (candidate, job) => {
    // Se candidato é de PAT, abrir dialog de carta de encaminhamento
    if (candidate.partner_id) {
      setCartaData({
        ...cartaData,
        candidate: candidate,
        job: job,
        data_entrevista: '',
        hora_entrevista: '',
        local_entrevista: '',
        entrevistador: '',
        observacoes: ''
      });
      setShowCartaDialog(true);
      return;
    }
    
    // Candidato normal - seleção direta
    try {
      await jobsAPI.selectCandidate(job.id, candidate.id);
      toast.success(`Candidato selecionado para "${job.titulo}"`);
      setSelectedCandidate(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao selecionar candidato');
    }
  };
  
  const handleCreateCarta = async () => {
    if (!cartaData.data_entrevista || !cartaData.hora_entrevista || !cartaData.local_entrevista || !cartaData.entrevistador) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    
    setSavingCarta(true);
    try {
      // Criar carta de encaminhamento
      await cartasAPI.create({
        candidate_id: cartaData.candidate.id,
        job_id: cartaData.job.id,
        data_entrevista: cartaData.data_entrevista,
        hora_entrevista: cartaData.hora_entrevista,
        local_entrevista: cartaData.local_entrevista,
        entrevistador: cartaData.entrevistador,
        documentos_necessarios: cartaData.documentos_necessarios,
        observacoes: cartaData.observacoes
      });
      
      // A carta vale como convite: o candidato é avisado e aceita no app
      toast.success('Carta criada e convite enviado ao candidato');
      setShowCartaDialog(false);
      setInviteRefresh((n) => n + 1);
    } catch (error) {
      console.error('Error creating carta:', error);
      toast.error(error.response?.data?.detail || 'Erro ao criar carta de encaminhamento');
    } finally {
      setSavingCarta(false);
    }
  };

  const activeFiltersCount = [
    filters.cargo,
    filters.localizacao,
    filters.disc_profile !== 'all' ? filters.disc_profile : '',
    filters.pcd !== 'all' ? filters.pcd : '',
    filters.estagiario !== 'all' ? filters.estagiario : '',
    filters.aprendiz !== 'all' ? filters.aprendiz : '',
    filters.eventual ? 'eventual' : ''
  ].filter(v => v).length;

  // Check feature access
  if (!hasAccess && !featureLoading) {
    return (
      <DashboardLayout menuItems={companyMenuItems}>
        <BlockedFeatureOverlay 
          feature="selecao_aberta"
          title="Seleção de Candidatos Bloqueada"
          description="O acesso à busca completa de candidatos não está liberado para sua conta. Entre em contato com o administrador para solicitar acesso a esta funcionalidade premium."
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={companyMenuItems}>
      <div className="space-y-6" data-testid="candidate-select">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Selecionar Candidatos</h1>
          <p className="text-slate-600">Busque talentos na base de candidatos</p>
        </div>

        <Tabs value={mainTab} onValueChange={setMainTab}>
          <TabsList>
            <TabsTrigger value="buscar" data-testid="candidate-tab-buscar">Buscar</TabsTrigger>
            <TabsTrigger value="por-cargo" data-testid="candidate-tab-por-cargo">Por Cargo</TabsTrigger>
          </TabsList>
          <TabsContent value="por-cargo" className="mt-4">
            {mainTab === 'por-cargo' && <CargoBannersTab onViewProfile={setSelectedCandidate} />}
          </TabsContent>
          <TabsContent value="buscar" className="mt-4 space-y-6">
        {/* Search Filters */}
        <Card>
          <CardContent className="p-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <Label htmlFor="cargo">Cargo</Label>
                <Input
                  id="cargo"
                  value={filters.cargo}
                  onChange={(e) => setFilters(prev => ({ ...prev, cargo: e.target.value }))}
                  placeholder="Ex: Desenvolvedor, Analista"
                />
              </div>
              <div>
                <Label htmlFor="localizacao">Localização</Label>
                <Input
                  id="localizacao"
                  value={filters.localizacao}
                  onChange={(e) => setFilters(prev => ({ ...prev, localizacao: e.target.value }))}
                  placeholder="Ex: São Paulo, Remoto"
                />
              </div>
              <div>
                <Label>Perfil DISC</Label>
                <Select 
                  value={filters.disc_profile} 
                  onValueChange={(v) => setFilters(prev => ({ ...prev, disc_profile: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Qualquer" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Qualquer</SelectItem>
                    <SelectItem value="D">D - Dominância</SelectItem>
                    <SelectItem value="I">I - Influência</SelectItem>
                    <SelectItem value="S">S - Estabilidade</SelectItem>
                    <SelectItem value="C">C - Conformidade</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button 
                  onClick={() => setShowFilters(!showFilters)}
                  variant="outline"
                  className="w-full"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Mais Filtros
                  {activeFiltersCount > 2 && (
                    <Badge className="ml-2 bg-cyan-100 text-cyan-700">{activeFiltersCount}</Badge>
                  )}
                </Button>
              </div>
            </div>

            {/* Additional filters */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t space-y-4">
                {/* Row 1: PcD, Estagiários, Aprendizes */}
                <div className="grid gap-4 sm:grid-cols-3">
                  <div>
                    <Label>Pessoas com Deficiência (PcD)</Label>
                    <Select 
                      value={filters.pcd} 
                      onValueChange={(v) => setFilters(prev => ({ ...prev, pcd: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="tambem">Incluir PcD</SelectItem>
                        <SelectItem value="somente">Somente PcD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Estagiários</Label>
                    <Select 
                      value={filters.estagiario} 
                      onValueChange={(v) => setFilters(prev => ({ ...prev, estagiario: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="tambem">Incluir Estagiários</SelectItem>
                        <SelectItem value="somente">Somente Estagiários</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Aprendizes</Label>
                    <Select 
                      value={filters.aprendiz} 
                      onValueChange={(v) => setFilters(prev => ({ ...prev, aprendiz: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        <SelectItem value="tambem">Incluir Aprendizes</SelectItem>
                        <SelectItem value="somente">Somente Aprendizes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Row 2: Trabalho Eventual */}
                <div className="p-4 rounded-lg border-2 border-dashed border-cyan-200 bg-cyan-50/50">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Clock className="w-5 h-5 text-cyan-600" />
                      <Label className="text-cyan-800 font-medium">Serviços Eventuais</Label>
                    </div>
                    <Switch
                      checked={filters.eventual}
                      onCheckedChange={(checked) => setFilters(prev => ({ 
                        ...prev, 
                        eventual: checked,
                        categoria_eventual: checked ? prev.categoria_eventual : 'all'
                      }))}
                      data-testid="filter-eventual-toggle"
                    />
                  </div>
                  <p className="text-sm text-cyan-700 mb-3">
                    Buscar apenas candidatos disponíveis para trabalhos eventuais (diárias, eventos)
                  </p>
                  
                  {filters.eventual && (
                    <div>
                      <Label className="text-cyan-700">Filtrar por categoria</Label>
                      <Select 
                        value={filters.categoria_eventual} 
                        onValueChange={(v) => setFilters(prev => ({ ...prev, categoria_eventual: v }))}
                      >
                        <SelectTrigger className="bg-white">
                          <SelectValue placeholder="Todas as categorias" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todas as categorias</SelectItem>
                          {categoriasEventual.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.nome} ({cat.grupo})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="mt-4 flex items-center gap-3">
              <Button 
                onClick={handleSearch}
                data-testid="candidate-search-btn"
                className="bg-cyan-600 hover:bg-cyan-700"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Search className="w-4 h-4 mr-2" />
                )}
                Buscar Candidatos
              </Button>
              {activeFiltersCount > 0 && (
                <Button variant="ghost" onClick={clearFilters}>
                  <X className="w-4 h-4 mr-2" />
                  Limpar Filtros
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
          </div>
        ) : candidates.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-16 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">
                Busque por candidatos
              </h3>
              <p className="text-slate-600">
                Use os filtros acima para encontrar candidatos que se encaixam nas suas vagas
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">
                {candidates.length} candidato(s) encontrado(s)
              </h2>
            </div>

            <div className="grid gap-4">
              {candidates.map((candidate) => (
                <Card 
                  key={candidate.id} 
                  className="border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedCandidate(candidate)}
                  data-testid={`candidate-item-${candidate.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                          <User className="w-6 h-6 text-slate-400" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900">{candidate.cargo}</h3>
                          <p className="text-slate-600 text-sm mb-2">{candidate.experiencia}</p>
                          
                          <div className="flex flex-wrap items-center gap-2">
                            {candidate.localizacao && (
                              <Badge variant="outline" className="text-xs">
                                <MapPin className="w-3 h-3 mr-1" />
                                {candidate.localizacao}
                              </Badge>
                            )}
                            {candidate.disc_profile && (
                              <Badge className="bg-purple-100 text-purple-700 text-xs">
                                <Brain className="w-3 h-3 mr-1" />
                                {candidate.disc_profile}
                              </Badge>
                            )}
                            {candidate.elegivel_inclusao && (
                              <Badge className="bg-pink-100 text-pink-700 text-xs">
                                <Heart className="w-3 h-3 mr-1" />
                                {candidate.elegivel_inclusao}
                              </Badge>
                            )}
                            {candidate.is_estagiario && (
                              <Badge className="bg-blue-100 text-blue-700 text-xs">
                                <GraduationCap className="w-3 h-3 mr-1" />
                                Estagiário
                              </Badge>
                            )}
                            {candidate.partner_id && partners[candidate.partner_id] && (
                              <Badge className="bg-amber-100 text-amber-700 text-xs">
                                <Landmark className="w-3 h-3 mr-1" />
                                {partners[candidate.partner_id].nome}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <Button variant="ghost" size="icon">
                        <ChevronRight className="w-5 h-5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

          </TabsContent>
        </Tabs>

        {/* Candidate Detail Sheet */}
        <Sheet open={!!selectedCandidate} onOpenChange={() => setSelectedCandidate(null)}>
          <SheetContent className="sm:max-w-lg overflow-y-auto">
            <SheetHeader>
              <SheetTitle>Detalhes do Candidato</SheetTitle>
              <SheetDescription>
                Visualize o perfil e selecione para uma vaga
              </SheetDescription>
            </SheetHeader>
            
            {selectedCandidate && (
              <div className="mt-6 space-y-6">
                {/* Candidate Info */}
                <CandidateContactCard candidateId={selectedCandidate.id} />
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold">{selectedCandidate.cargo}</h3>
                    {selectedCandidate.localizacao && (
                      <p className="text-slate-600 text-sm flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {selectedCandidate.localizacao}
                      </p>
                    )}
                  </div>

                  {selectedCandidate.experiencia && (
                    <div>
                      <h4 className="font-medium text-slate-900 mb-1">Experiência</h4>
                      <p className="text-slate-600 text-sm">{selectedCandidate.experiencia}</p>
                    </div>
                  )}

                  {selectedCandidate.disc_profile && (
                    <div>
                      <h4 className="font-medium text-slate-900 mb-1">Perfil DISC</h4>
                      <Badge className="bg-purple-100 text-purple-700">
                        <Brain className="w-3 h-3 mr-1" />
                        {selectedCandidate.disc_profile}
                      </Badge>
                    </div>
                  )}

                  {selectedCandidate.competencias?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-slate-900 mb-2">Competências</h4>
                      <div className="flex flex-wrap gap-1">
                        {selectedCandidate.competencias.map((comp, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{comp}</Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedCandidate.elegivel_inclusao && (
                    <div className="p-3 bg-pink-50 rounded-lg">
                      <p className="text-pink-700 text-sm flex items-center gap-2">
                        <Heart className="w-4 h-4" />
                        {selectedCandidate.elegivel_inclusao}
                      </p>
                    </div>
                  )}
                </div>

                {/* Select for Job */}
                <div className="border-t pt-4">
                  <h4 className="font-medium text-slate-900 mb-1">Vagas</h4>
                  <p className="text-xs text-slate-500 mb-3">Fixe direto na vaga ou envie um convite (aviso no app e por e-mail). Não é preciso esperar o aceite.</p>
                  
                  {/* Aviso se candidato é de PAT */}
                  {selectedCandidate.partner_id && partners[selectedCandidate.partner_id] && (
                    <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-amber-800 text-sm">
                        <Landmark className="w-4 h-4 inline mr-1 -mt-0.5" />
                        Este candidato é do <strong>{partners[selectedCandidate.partner_id].nome}</strong>. 
                        O convite é feito pela Carta de Encaminhamento.
                      </p>
                    </div>
                  )}
                  
                  {jobs.length === 0 ? (
                    <p className="text-slate-500 text-sm">Nenhuma vaga ativa disponível</p>
                  ) : (
                    <JobInvitePanel candidate={selectedCandidate} jobs={jobs} refreshKey={inviteRefresh}
                      onPartnerInvite={(job) => handleSelectForJob(selectedCandidate, job)} />
                  )}
                </div>
              </div>
            )}
          </SheetContent>
        </Sheet>
        
        {/* Dialog - Carta de Encaminhamento */}
        <Dialog open={showCartaDialog} onOpenChange={setShowCartaDialog}>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-500" />
                Carta de Encaminhamento
              </DialogTitle>
              <DialogDescription>
                Preencha os dados para gerar a carta de encaminhamento do PAT
              </DialogDescription>
            </DialogHeader>
            
            {cartaData.candidate && cartaData.job && (
              <div className="space-y-4">
                {/* Info do candidato e vaga */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-xs text-slate-500">Candidato</p>
                    <p className="font-medium">{cartaData.candidate.nome || cartaData.candidate.cargo}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Vaga</p>
                    <p className="font-medium">{cartaData.job.titulo}</p>
                  </div>
                </div>
                
                {/* PAT de origem */}
                {partners[cartaData.candidate.partner_id] && (
                  <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <Landmark className="w-5 h-5 text-amber-600" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">
                        {partners[cartaData.candidate.partner_id].nome}
                      </p>
                      <p className="text-xs text-amber-600">
                        {partners[cartaData.candidate.partner_id].municipio}/{partners[cartaData.candidate.partner_id].estado}
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Formulário */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Data da Entrevista *</Label>
                    <Input
                      type="date"
                      value={cartaData.data_entrevista}
                      onChange={(e) => setCartaData({...cartaData, data_entrevista: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Hora *</Label>
                    <Input
                      type="time"
                      value={cartaData.hora_entrevista}
                      onChange={(e) => setCartaData({...cartaData, hora_entrevista: e.target.value})}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Local da Entrevista *</Label>
                  <Input
                    value={cartaData.local_entrevista}
                    onChange={(e) => setCartaData({...cartaData, local_entrevista: e.target.value})}
                    placeholder="Endereço completo onde será a entrevista"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Entrevistador *</Label>
                  <Input
                    value={cartaData.entrevistador}
                    onChange={(e) => setCartaData({...cartaData, entrevistador: e.target.value})}
                    placeholder="Nome do responsável pela entrevista"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Documentos Necessários</Label>
                  <Input
                    value={cartaData.documentos_necessarios}
                    onChange={(e) => setCartaData({...cartaData, documentos_necessarios: e.target.value})}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Observações</Label>
                  <Textarea
                    value={cartaData.observacoes}
                    onChange={(e) => setCartaData({...cartaData, observacoes: e.target.value})}
                    placeholder="Informações adicionais para o candidato..."
                    rows={2}
                  />
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCartaDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleCreateCarta}
                data-testid="carta-submit"
                disabled={savingCarta}
                className="bg-amber-500 hover:bg-amber-600"
              >
                {savingCarta ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileText className="w-4 h-4 mr-2" />
                )}
                Gerar Carta e Convidar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
